import React, { useState, useEffect } from 'react';
import { getTenants } from '../../services/adminApiService';

const PropelOrgForm = ({ propelOrg, onSubmit, onCancel }) => {
  const [tenants, setTenants] = useState([]);
  const [formData, setFormData] = useState({
    onshapeTenantId: '',
    orgName: '',
    orgLabel: '',
    propelClientId: '',
    propelClientSecret: '',
    propelInstanceUrl: '',
    propelRedirectUrl: '',
    propelOauthUrl: '',
    propelNamespace: 'PDLM',
    isDefault: false,
    displayOrder: 0,
    notes: '',
    features: {
      enablePushAttachments: false,
      enablePushBom: false,
      enableStagedImportReview: false,
      maxItemsPerImportSession: 20,
      exportNamingRules: []
    }
  });

  useEffect(() => {
    loadTenants();
  }, []);

  useEffect(() => {
    if (propelOrg) {
      setFormData({
        onshapeTenantId: propelOrg.onshape_tenant_id || '',
        orgName: propelOrg.org_name || '',
        orgLabel: propelOrg.org_label || '',
        propelClientId: propelOrg.propel_client_id || '',
        propelClientSecret: '', // Don't populate secret on edit
        propelInstanceUrl: propelOrg.propel_instance_url || '',
        propelRedirectUrl: propelOrg.propel_redirect_url || '',
        propelOauthUrl: propelOrg.propel_oauth_url || '',
        propelNamespace: propelOrg.propel_namespace || 'PDLM',
        isDefault: propelOrg.is_default || false,
        displayOrder: propelOrg.display_order || 0,
        notes: propelOrg.notes || '',
        features: propelOrg.features ? {
          enablePushAttachments: propelOrg.features.enablePushAttachments ?? false,
          enablePushBom: propelOrg.features.enablePushBom ?? false,
          enableStagedImportReview: propelOrg.features.enableStagedImportReview ?? false,
          maxItemsPerImportSession: propelOrg.features.maxItemsPerImportSession ?? 20,
          exportNamingRules: propelOrg.features.exportNamingRules || []
        } : {
          enablePushAttachments: false,
          enablePushBom: false,
          enableStagedImportReview: false,
          maxItemsPerImportSession: 20,
          exportNamingRules: []
        }
      });
    }
  }, [propelOrg]);

  async function loadTenants() {
    try {
      const response = await getTenants(false);
      setTenants(response.tenants || []);
    } catch (error) {
      console.error('Error loading tenants:', error);
    }
  }

  function handleFeatureChange(e) {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      features: {
        ...prev.features,
        [name]: type === 'checkbox' ? checked : Number(value)
      }
    }));
  }

  function addExportRule() {
    setFormData(prev => ({
      ...prev,
      features: {
        ...prev.features,
        exportNamingRules: [
          ...(prev.features.exportNamingRules || []),
          { scope: 'Part', convention: '${name}' }
        ]
      }
    }));
  }

  function removeExportRule(index) {
    setFormData(prev => ({
      ...prev,
      features: {
        ...prev.features,
        exportNamingRules: (prev.features.exportNamingRules || []).filter((_, i) => i !== index)
      }
    }));
  }

  function updateExportRule(index, field, value) {
    setFormData(prev => {
      const rules = (prev.features.exportNamingRules || []).map((r, i) =>
        i === index ? { ...r, [field]: value } : r
      );
      return { ...prev, features: { ...prev.features, exportNamingRules: rules } };
    });
  }

  function handleInputChange(e) {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  }

  function handleSubmit(e) {
    e.preventDefault();

    // For updates, only send changed fields
    if (propelOrg) {
      const updates = {};

      if (formData.orgName !== propelOrg.org_name) {
        updates.orgName = formData.orgName;
      }
      if (formData.orgLabel !== propelOrg.org_label) {
        updates.orgLabel = formData.orgLabel;
      }
      if (formData.propelClientId !== propelOrg.propel_client_id) {
        updates.propelClientId = formData.propelClientId;
      }
      // Only include secret if it was changed
      if (formData.propelClientSecret) {
        updates.propelClientSecret = formData.propelClientSecret;
      }
      if (formData.propelInstanceUrl !== (propelOrg.propel_instance_url || '')) {
        updates.propelInstanceUrl = formData.propelInstanceUrl;
      }
      if (formData.propelRedirectUrl !== propelOrg.propel_redirect_url) {
        updates.propelRedirectUrl = formData.propelRedirectUrl;
      }
      if (formData.propelOauthUrl !== propelOrg.propel_oauth_url) {
        updates.propelOauthUrl = formData.propelOauthUrl;
      }
      if (formData.propelNamespace !== propelOrg.propel_namespace) {
        updates.propelNamespace = formData.propelNamespace;
      }
      if (formData.displayOrder !== propelOrg.display_order) {
        updates.displayOrder = parseInt(formData.displayOrder, 10);
      }
      if (formData.notes !== (propelOrg.notes || '')) {
        updates.notes = formData.notes;
      }

      const currentFeatures = propelOrg.features || {};
      const normalizedCurrent = {
        enablePushAttachments: currentFeatures.enablePushAttachments ?? false,
        enablePushBom: currentFeatures.enablePushBom ?? false,
        enableStagedImportReview: currentFeatures.enableStagedImportReview ?? false,
        maxItemsPerImportSession: currentFeatures.maxItemsPerImportSession ?? 20,
        exportNamingRules: currentFeatures.exportNamingRules || []
      };
      if (JSON.stringify(formData.features) !== JSON.stringify(normalizedCurrent)) {
        updates.features = formData.features;
      }

      onSubmit(updates);
    } else {
      // For creates, send all data
      onSubmit({
        ...formData,
        onshapeTenantId: parseInt(formData.onshapeTenantId, 10),
        displayOrder: parseInt(formData.displayOrder, 10)
      });
    }
  }

  return (
    <div className="form-container">
      <div className="form-header">
        <h3>{propelOrg ? 'Edit Propel Organization' : 'Create New Propel Organization'}</h3>
      </div>

      <form onSubmit={handleSubmit} className="admin-form">
        <div className="form-group">
          <label htmlFor="onshapeTenantId">
            Onshape Tenant <span className="required">*</span>
          </label>
          <select
            id="onshapeTenantId"
            name="onshapeTenantId"
            value={formData.onshapeTenantId}
            onChange={handleInputChange}
            required
            disabled={!!propelOrg} // Cannot change tenant on update
          >
            <option value="">Select a tenant</option>
            {tenants.map((tenant) => (
              <option key={tenant.id} value={tenant.id}>
                {tenant.company_name} ({tenant.onshape_company_id})
              </option>
            ))}
          </select>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="orgName">
              Org Name <span className="required">*</span>
            </label>
            <input
              type="text"
              id="orgName"
              name="orgName"
              value={formData.orgName}
              onChange={handleInputChange}
              required
              placeholder="e.g., AcmePropel"
            />
            <small className="help-text">Internal name for this org</small>
          </div>

          <div className="form-group">
            <label htmlFor="orgLabel">
              Display Label <span className="required">*</span>
            </label>
            <input
              type="text"
              id="orgLabel"
              name="orgLabel"
              value={formData.orgLabel}
              onChange={handleInputChange}
              required
              placeholder="e.g., Production"
            />
            <small className="help-text">User-facing label</small>
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="propelClientId">
            Propel Client ID <span className="required">*</span>
          </label>
          <input
            type="text"
            id="propelClientId"
            name="propelClientId"
            value={formData.propelClientId}
            onChange={handleInputChange}
            required
            placeholder="Salesforce Connected App Consumer Key"
          />
        </div>

        <div className="form-group">
          <label htmlFor="propelClientSecret">
            Propel Client Secret {propelOrg ? '' : <span className="required">*</span>}
          </label>
          <input
            type="password"
            id="propelClientSecret"
            name="propelClientSecret"
            value={formData.propelClientSecret}
            onChange={handleInputChange}
            required={!propelOrg}
            placeholder={propelOrg ? 'Leave blank to keep existing secret' : 'Salesforce Connected App Consumer Secret'}
          />
          {propelOrg && (
            <small className="help-text">Leave blank to keep the existing secret</small>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="propelInstanceUrl">Propel Instance URL</label>
          <input
            type="url"
            id="propelInstanceUrl"
            name="propelInstanceUrl"
            value={formData.propelInstanceUrl}
            onChange={handleInputChange}
            placeholder="e.g., https://acme.my.salesforce.com"
          />
          <small className="help-text">Your Salesforce instance URL (optional)</small>
        </div>

        <div className="form-group">
          <label htmlFor="propelRedirectUrl">
            Propel Redirect URL <span className="required">*</span>
          </label>
          <input
            type="url"
            id="propelRedirectUrl"
            name="propelRedirectUrl"
            value={formData.propelRedirectUrl}
            onChange={handleInputChange}
            required
            placeholder="e.g., https://your-app.herokuapp.com/api/propel/oauth/callback"
          />
          <small className="help-text">OAuth callback URL configured in Salesforce</small>
        </div>

        <div className="form-group">
          <label htmlFor="propelOauthUrl">
            Propel OAuth URL <span className="required">*</span>
          </label>
          <input
            type="url"
            id="propelOauthUrl"
            name="propelOauthUrl"
            value={formData.propelOauthUrl}
            onChange={handleInputChange}
            required
            placeholder="e.g., https://login.salesforce.com"
          />
          <small className="help-text">Usually https://login.salesforce.com or https://test.salesforce.com</small>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="propelNamespace">Propel Namespace</label>
            <input
              type="text"
              id="propelNamespace"
              name="propelNamespace"
              value={formData.propelNamespace}
              onChange={handleInputChange}
              placeholder="PDLM"
            />
            <small className="help-text">Salesforce package namespace (usually PDLM)</small>
          </div>

          <div className="form-group">
            <label htmlFor="displayOrder">Display Order</label>
            <input
              type="number"
              id="displayOrder"
              name="displayOrder"
              value={formData.displayOrder}
              onChange={handleInputChange}
              min="0"
              placeholder="0"
            />
            <small className="help-text">Sort order in dropdown</small>
          </div>
        </div>

        <div className="form-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              name="isDefault"
              checked={formData.isDefault}
              onChange={handleInputChange}
            />
            Set as default organization for this tenant
          </label>
        </div>

        <div className="form-group">
          <label>Feature Flags</label>
          <small className="help-text" style={{ display: 'block', marginBottom: 8 }}>
            Overrides the global Heroku env var defaults for this Propel org.
          </small>
          <label className="checkbox-label">
            <input type="checkbox" name="enablePushAttachments"
              checked={formData.features.enablePushAttachments}
              onChange={handleFeatureChange} />
            Enable Push Attachments
          </label>
          <label className="checkbox-label">
            <input type="checkbox" name="enablePushBom"
              checked={formData.features.enablePushBom}
              onChange={handleFeatureChange} />
            Enable Export BOM
          </label>
          <label className="checkbox-label">
            <input type="checkbox" name="enableStagedImportReview"
              checked={formData.features.enableStagedImportReview}
              onChange={handleFeatureChange} />
            Enable Staged Import Review
          </label>
          <div className="form-row" style={{ marginTop: 8 }}>
            <div className="form-group">
              <label htmlFor="maxItemsPerImportSession">Max Items Per Import Session</label>
              <input type="number" id="maxItemsPerImportSession" name="maxItemsPerImportSession"
                value={formData.features.maxItemsPerImportSession}
                onChange={handleFeatureChange}
                min="1" max="200" />
              <small className="help-text">Default: 20</small>
            </div>
          </div>
        </div>

        {/* Export Naming Rules */}
        <div className="form-section">
          <h4>Export Naming Rules</h4>
          <p className="help-text">
            Controls how exported STEP/PDF filenames are constructed.
            Default: <code>{'${category}-${partNumber}-${revision} ${description}'}</code>.
            Available variables: <code>{'${name}'}</code>, <code>{'${category}'}</code>, <code>{'${partNumber}'}</code>, <code>{'${revision}'}</code>, <code>{'${description}'}</code>
          </p>
          {(formData.features.exportNamingRules || []).map((rule, idx) => (
            <div key={idx} style={{ display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'center' }}>
              <select
                value={rule.scope}
                onChange={e => updateExportRule(idx, 'scope', e.target.value)}
                style={{ width: '120px' }}
              >
                <option value="Part">Part</option>
                <option value="Assembly">Assembly</option>
                <option value="All">All</option>
              </select>
              <input
                type="text"
                value={rule.convention}
                onChange={e => updateExportRule(idx, 'convention', e.target.value)}
                placeholder="${category}-${partNumber}-${revision} ${description}"
                style={{ flex: 1 }}
              />
              <button type="button" onClick={() => removeExportRule(idx)} style={{ color: 'red' }}>Remove</button>
            </div>
          ))}
          <button type="button" onClick={addExportRule}>+ Add Rule</button>
        </div>

        <div className="form-group">
          <label htmlFor="notes">Notes</label>
          <textarea
            id="notes"
            name="notes"
            value={formData.notes}
            onChange={handleInputChange}
            rows="4"
            placeholder="Optional notes about this Propel organization..."
          />
        </div>

        <div className="form-actions">
          <button type="button" onClick={onCancel} className="btn btn-secondary">
            Cancel
          </button>
          <button type="submit" className="btn btn-primary">
            {propelOrg ? 'Update Propel Org' : 'Create Propel Org'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default PropelOrgForm;
